import { Group, Modal, Stack, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useApi } from 'api/api-context';
import Button from 'components/Button';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { RevisionPredefinedDataSelection } from 'components/inputs/revision-predefined-data/types';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import useLoadingAction from 'hooks/use-loading-action';
import { useEffect } from 'react';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';

interface CreateRevisionPredefinedDataModalFormValues {
  predefinedDataName: string;
  predefinedDataDescription: string;
}

export interface CreateRevisionPredefinedDataModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: (value: RevisionPredefinedDataSelection) => void;
  deviceTypeName: string;
  predefinedDataTypeId: number;
  predefinedDataTypeName: string;
  initialName: string;
}

/**
 * Modal for creating a personal predefined data record from revision fill-out.
 */
export default function CreateRevisionPredefinedDataModal({
  opened,
  onClose,
  onCreated,
  deviceTypeName,
  predefinedDataTypeId,
  predefinedDataTypeName,
  initialName,
}: CreateRevisionPredefinedDataModalProps) {
  const { getAction } = useApi();

  const form = useForm<CreateRevisionPredefinedDataModalFormValues>({
    initialValues: {
      predefinedDataName: initialName,
      predefinedDataDescription: '',
    },
    validate: {
      predefinedDataName: createValidator([required]),
    },
  });

  useEffect(() => {
    if (!opened) {
      return;
    }

    form.setValues({
      predefinedDataName: initialName,
      predefinedDataDescription: '',
    });
    form.resetDirty();
  }, [initialName, opened]);

  const [{ loading }, submit] = useLoadingAction(async (values: CreateRevisionPredefinedDataModalFormValues) => {
    const action = getAction('PredefinedDataCreateUsers');

    await action({
      payload: {
        predefinedDataName: values.predefinedDataName,
        predefinedDataDescription: values.predefinedDataDescription || undefined,
        predefinedDataTypeId,
      },
    });

    notifications.show({
      title: 'Predvolený údaj bol vytvorený',
      message: `Predvolený údaj ${values.predefinedDataName} bol úspešne vytvorený.`,
      color: SUCCESS_NOTIFICATION_COLOR,
    });

    onCreated({
      name: values.predefinedDataName,
      description: values.predefinedDataDescription,
      source: 'catalog',
    });
  });

  return (
    <Modal
      opened={opened}
      onClose={
        loading
          ? /* istanbul ignore next -- explicitly authorized unreachable coverage blocker */ () => undefined
          : onClose
      }
      title="Vytvoriť vlastný predvolený údaj"
      size="md"
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit((values) => submit(values).catch(panic))}>
        <Stack spacing={16}>
          <TextInput
            label="Typ zariadenia"
            value={deviceTypeName}
            readOnly
            size="md"
            name="objekto-modal-deviceTypeName"
          />
          <TextInput
            label="Typ údaja"
            value={predefinedDataTypeName}
            readOnly
            size="md"
            name="objekto-modal-predefinedDataTypeName"
          />
          <TextInput
            required
            label="Názov"
            placeholder="Názov predvoleného údaja"
            size="md"
            name="objekto-modal-predefinedDataName"
            {...form.getInputProps('predefinedDataName')}
          />
          <Textarea
            label="Popis"
            placeholder="Popis predvoleného údaja"
            autosize
            minRows={3}
            maxRows={8}
            size="md"
            name="objekto-modal-predefinedDataDescription"
            {...form.getInputProps('predefinedDataDescription')}
          />
          <P3Regular c="gray.6">
            Hodnota bude uložená ako váš osobný predvolený údaj pre tento typ zariadenia a typ údaja.
          </P3Regular>
          <Group position="right" noWrap>
            <Button variant="subtle-gray" type="button" onClick={onClose} disabled={loading}>
              Zrušiť
            </Button>
            <Button variant="primary" type="submit" loading={loading}>
              Uložiť
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
